/* eslint-disable @typescript-eslint/ban-ts-comment */
import JitsiStreamBackgroundEffect, {
  IBackgroundEffectOptions,
} from './JitsiStreamBackgroundEffect';
// @ts-ignore
import createTFLiteModule from '../../../public/vendor/tflite/tflite';
// @ts-ignore
import createTFLiteSIMDModule from '../../../public/vendor/tflite/tflite-simd';
import { timeout } from './helpers';
import { VIRTUAL_BACKGROUND_TYPE, VirtualBackgroundOptions } from '../../apps/meeting/types';

const models = {
  modelLandscape: '../vendor/models/selfie_segmentation_landscape.tflite',
};

let modelBuffer: ArrayBuffer;
let tflite: any;
let isWasmDisabled = false;

const segmentationDimensions = {
  modelLandscape: {
    height: 144,
    width: 256,
  },
};

export async function createVirtualBackgroundEffect(
  virtualBackground: IBackgroundEffectOptions['virtualBackground']
): Promise<JitsiStreamBackgroundEffect | undefined> {
  if (!MediaStreamTrack.prototype.getSettings && !MediaStreamTrack.prototype.getConstraints) {
    throw new Error('JitsiStreamBackgroundEffect not supported!');
  }

  if (isWasmDisabled) {
    console.warn('WebAssembly is disabled or not supported');
    return;
  }

  if (!tflite) {
    try {
      const wasmCheck = await import('wasm-check');
      const tfliteTimeout = 10000;
      if (wasmCheck?.feature?.simd) {
        tflite = await timeout(
          tfliteTimeout,
          createTFLiteSIMDModule({
            locateFile: (path: string) => '/vendor/tflite/' + path,
          })
        );
      } else {
        tflite = await timeout(
          tfliteTimeout,
          createTFLiteModule({
            locateFile: (path: string) => '/vendor/tflite/' + path,
          })
        );
      }
    } catch (err: any) {
      if (err?.message === '408') {
        console.error('Failed to download tflite model!');
      } else {
        isWasmDisabled = true;
        console.error('Looks like WebAssembly is disabled or not supported on this browser', err);
      }
      return;
    }
  }

  if (!modelBuffer) {
    const modelResponse = await fetch(models.modelLandscape);

    if (!modelResponse.ok) {
      throw new Error('Failed to download tflite model!');
    }

    modelBuffer = await modelResponse.arrayBuffer();

    tflite.HEAPU8.set(new Uint8Array(modelBuffer), tflite._getModelBufferMemoryOffset());
    tflite._loadModel(modelBuffer.byteLength);
  }

  const options = {
    ...segmentationDimensions.modelLandscape,
    virtualBackground,
  };

  return new JitsiStreamBackgroundEffect(tflite, options);
}

export const effectFactory = async (
  state: VirtualBackgroundOptions
): Promise<JitsiStreamBackgroundEffect | undefined> => {
  const virtualBackground: IBackgroundEffectOptions['virtualBackground'] = {
    backgroundType: state.backgroundType,
    blurValue: state.blurValue,
    virtualSource:
      state.backgroundType === VIRTUAL_BACKGROUND_TYPE.IMAGE ? state.virtualSource : undefined,
  };

  if (!state.backgroundEffectEnabled) {
    return undefined;
  }

  try {
    return await createVirtualBackgroundEffect(virtualBackground);
  } catch (error) {
    console.error('Failed to create virtual background effect:', error);
    return undefined;
  }
};
